import {
  DatabaseAuto,
  IAnalyticsResultExpense,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser,
  IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  Ireceipt,
  TpayType,
  TreceiptRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for receipt properties with specific generic types
 * @template T - Type for receipt (default: Ireceipt<number, IcommerceUser>)
 */
type TreceiptProps = Ireceipt<number, IcommerceUser>;

/**
 * Represents a receipt in the system.
 * This class provides methods to manage and track receipts,
 * including creation, retrieval, updating, and deletion of receipts.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TreceiptProps - The type definition for receipt properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Receipt} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const receipt = await Receipt.getAll();
 *
 * const receipt = await Receipt.filterAll({
 *
 * });
 *
 * const receipt = await Receipt.bulkDelete({
 *
 * });
 *
 * const receipt = await Receipt.getOne('resourceId');
 *
 * const receipt = await Receipt.remove();
 *
 * ```
 */
export class Receipt extends DatabaseAuto {
  /** Base API endpoint for receipt operations */
  static readonly baseUrl = '/receipt';

  /** Unique reference ID for the receipt */
  readonly urId: TreceiptProps['urId'];

  /** Payment mode used for the receipt */
  paymentMode: TreceiptProps['paymentMode'];

  /** Type of receipt (e.g., ecommerce, traditional, subscription) */
  receiptType: TreceiptProps['receiptType'];

  /** Total amount of the receipt */
  amount: TreceiptProps['amount'];

  /** Current status of the receipt */
  receiptStatus: TreceiptProps['receiptStatus'];

  /** Related invoice information */
  invoice: TreceiptProps['invoice'];

  /** Unique number identifying the receipt */
  receiptNumber: TreceiptProps['receiptNumber'];

  /** Type of payment (e.g., cash, card, bank transfer) */
  payType?: TpayType;

  /** E-commerce vendors associated with this receipt */
  ecommerceVendors: TreceiptProps['ecommerceVendors'];

  /** Date when the receipt was issued */
  receiptDate: TreceiptProps['receiptDate'];

  /** Company associated with the receipt */
  readonly company: TreceiptProps['company'];

  /**
   * Creates a new instance of Receipt.
   * @param {TreceiptProps} data - The receipt data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TreceiptProps) {
    super(data);

    this.urId = data.urId;
    this.ecommerceVendors = data.ecommerceVendors;
    this.paymentMode = data.paymentMode;
    this.receiptType = data.receiptType || 'traditional';
    this.amount = data.amount;
    this.receiptStatus = data.receiptStatus;
    this.invoice = data.invoice;
    this.receiptNumber = data.receiptNumber;
    this.payType = data.payType;
    this.receiptDate = data.receiptDate;
    this.company = data.company;
  }

  /**
   * Retrieves receipt analytics based on the provided filter criteria.
   * @param {IfilterProps} filter - The filter criteria for analytics
   * @returns {Promise<IAnalyticsResultExpense>} The analytics result
   * @throws {Error} If the analytics request fails
   */
  static getReceiptAnalytics(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IAnalyticsResultExpense>(
        `${Receipt.baseUrl}/analytics`,
        filter
      );

    return lastValueFrom(observer$);
  }

  /**
   * Retrieves all receipts with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Receipt[] }>}
   * An object containing the total count and an array of receipts
   * @throws {Error} If there's an error fetching the receipts
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();

    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TreceiptProps>>(`${Receipt.baseUrl}?${query}`);

    const receipts = await lastValueFrom(observer$);

    return {
      count: receipts.count,
      dataArr: receipts.data.map(val => new Receipt(val))
    };
  }

  /**
   * Filters receipts based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Receipt[] }>}
   * An object containing the total count and an array of filtered receipts
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TreceiptProps>>(
        `${Receipt.baseUrl}/filter`,
        filter
      );

    const receipts = await lastValueFrom(observer$);

    return {
      count: receipts.count,
      dataArr: receipts.data.map(val => new Receipt(val))
    };
  }

  /**
   * Retrieves a single receipt by its ID.
   * @param {string} resourceId - The unique identifier of the receipt to retrieve
   * @returns {Promise<Receipt>} The requested receipt
   * @throws {Error} If the receipt is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TreceiptProps>(`${Receipt.baseUrl}/${resourceId}`);
    const receipt = await lastValueFrom(observer$);

    return new Receipt(receipt);
  }

  /**
   * Creates a new receipt record.
   * @param {TreceiptRequest<true>} payload - The receipt data to create
   * @returns {Promise<Receipt>} The newly created receipt
   * @throws {Error} If the creation fails
   */
  static async add(payload: TreceiptRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TreceiptProps>(`${Receipt.baseUrl}`, payload);

    return new Receipt(await lastValueFrom(observer$));
  }

  /**
   * Deletes multiple receipts based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which receipts to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${Receipt.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current receipt with new values.
   * @param {TreceiptRequest} payload - The data to update the receipt with
   * @returns {Promise<Receipt>} The updated receipt
   * @throws {Error} If the update operation fails
   */
  async update(payload: TreceiptRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TreceiptProps>(
        `${Receipt.baseUrl}/${this._id}`,
        payload
      );

    return new Receipt(await lastValueFrom(observer$));
  }

  /**
   * Removes the current receipt from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the receipt doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Receipt.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
